import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowDownIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, DatabaseIcon, LayoutDashboardIcon, TargetIcon, CodeIcon, WrenchIcon, ServerIcon, BarChart3Icon, AlertTriangleIcon, TrendingDownIcon, LinkIcon, DollarSignIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const governanceCards = [{
  icon: <LayoutDashboardIcon className="h-6 w-6 text-nrs-orange" data-id="element-313" />,
  title: 'Technical Policy Governance',
  desc: 'Architecture standards, code review, upgrade-safe design'
}, {
  icon: <DatabaseIcon className="h-6 w-6 text-nrs-orange" data-id="element-314" />,
  title: 'Data Governance',
  desc: 'Master data ownership, integration patterns, cross-domain sync'
}, {
  icon: <ShieldCheckIcon className="h-6 w-6 text-nrs-orange" data-id="element-315" />,
  title: 'Security & ATO Governance',
  desc: 'Continuous ATO readiness, RMF compliance, security control validation'
}, {
  icon: <TargetIcon className="h-6 w-6 text-nrs-orange" data-id="element-316" />,
  title: 'Strategic Governance',
  desc: 'Application rationalization, business case validation, ROI measurement'
}];
const developmentCards = [{
  icon: <CodeIcon className="h-6 w-6 text-nrs-blue" data-id="element-317" />,
  title: 'Platform Development',
  desc: 'ITSM, HRSD, SPM, Security Operations, custom applications'
}, {
  icon: <WrenchIcon className="h-6 w-6 text-nrs-blue" data-id="element-318" />,
  title: 'Specialized Development',
  desc: 'Complex integrations, custom modules, cross-domain solutions'
}, {
  icon: <ServerIcon className="h-6 w-6 text-nrs-blue" data-id="element-319" />,
  title: 'Platform Operations',
  desc: 'Day-to-day administration, ongoing enhancements, support'
}, {
  icon: <BarChart3Icon className="h-6 w-6 text-nrs-blue" data-id="element-320" />,
  title: 'Application Migration',
  desc: 'Legacy system consolidation, data migration, integration buildout'
}];
const governanceGaps = [{
  title: 'Technical Policy Governance',
  items: ['No architecture review process before development starts', 'No coding standards or best practices enforcement', 'No upgrade-safety validation', 'Heavy customization creating technical debt']
}, {
  title: 'Data Governance',
  items: ['No master data ownership across integrations', 'Inconsistent data models between modules', 'No strategy for cross-domain sync (NIPR/JWICS)', 'Integration sprawl with no patterns or standards']
}, {
  title: 'Security & ATO Governance',
  items: ['No pre-change security impact analysis', 'Developers introducing changes that jeopardize platform ATO', 'No continuous RMF compliance monitoring', 'ATO re-authorization delays due to undocumented changes']
}, {
  title: 'Strategic Governance',
  items: ['No independent application rationalization', 'No objective business case validation for new modules', 'No ROI measurement framework', 'SIs incentivized to build everything regardless of fit']
}];
const consequences = [{
  icon: '🔧',
  label: 'Technical Debt',
  desc: 'Customizations that block upgrades and create security vulnerabilities'
}, {
  icon: '💸',
  label: 'Wasted Investment',
  desc: "Building applications on ServiceNow that shouldn't be there"
}, {
  icon: '🔗',
  label: 'Integration Chaos',
  desc: '50+ point-to-point integrations with no governance'
}, {
  icon: '⚠️',
  label: 'ATO Jeopardy',
  desc: 'Platform changes threatening accreditation status'
}, {
  icon: '📊',
  label: 'Compliance Risk',
  desc: 'Inability to demonstrate data lineage or security posture'
}];
const comparisonRows = [{
  traditional: 'Impact provides product vision only; SI provides development only',
  nrs: 'We provide BOTH governance AND development'
}, {
  traditional: 'Large SI builds whatever you approve with no independent validation',
  nrs: 'We build WITH built-in governance (self-governing development)'
}, {
  traditional: 'No independent application rationalization',
  nrs: 'We assess your entire portfolio objectively BEFORE building'
}, {
  traditional: 'Separate vendors for governance vs. development creates friction',
  nrs: 'Single partner for both = alignment and accountability'
}, {
  traditional: 'Focus on feature delivery without governance',
  nrs: 'Every project includes architecture review, data governance, and ATO validation'
}];
const proofPoints = [{
  metric: 'Application rationalization ROI',
  result: '3:1 average savings'
}, {
  metric: 'Applications evaluated',
  result: '50-150 per engagement; 30-40% strong fit'
}, {
  metric: 'Legacy decommissioning savings',
  result: '$200K-600K annual savings'
}, {
  metric: 'Technical policy compliance',
  result: '95%+ first-submission approval'
}, {
  metric: 'Data governance maturity',
  result: '100% master data ownership in 90 days'
}, {
  metric: 'Upgrade readiness improvement',
  result: 'Instance Scan: 60s → 85+'
}, {
  metric: 'Cross-domain sync accuracy',
  result: '<2% data discrepancy rate'
}, {
  metric: 'Migration fit accuracy',
  result: '<5% regret rate on decisions'
}];
const faqs = [{
  q: 'How is this different from ServiceNow Impact?',
  a: 'Impact provides product-line vision and adoption roadmaps for ITSM, HRSD, SPM, etc. We provide the governance layer: technical policy (architecture standards), data governance (master data ownership, integration patterns), and strategic governance (application rationalization, business case validation). We\'re complementary—Impact drives the "what\'s possible," we ensure the "how to do it right and whether you should."'
}, {
  q: "Won't this slow down our SI's development work?",
  a: 'Initially, yes—architecture review adds 1-2 days per project. But it prevents 3-6 month rework cycles when customizations break upgrades or integrations fail. Net result: 30-40% faster time-to-value because you build the right thing once instead of the wrong thing three times.'
}, {
  q: "What if your application rationalization says we SHOULDN'T migrate something to ServiceNow?",
  a: "That's the point. We're independent. If an application is a poor fit (specialized functionality, better standalone tools exist, ROI is negative), we'll tell you. Unlike SIs who profit from migration work, our incentive is accuracy, not billable hours."
}, {
  q: 'Can we still work with our current SI?',
  a: 'Absolutely. We complement your SI—they do the implementation work, we do the governance oversight. Think of it as an independent code review process. Good SIs actually appreciate having clear architecture standards and governance processes.'
}, {
  q: 'Do you replace ServiceNow Impact consultants?',
  a: 'No. Impact focuses on product adoption and strategic vision for specific modules (ITSM, HRSD, etc.). We focus on technical policy governance across ALL modules, data governance for integrations, and strategic governance for application portfolio rationalization. We coordinate with Impact, not compete.'
}, {
  q: "What if we're on an old version and stuck?",
  a: "That's a governance failure (technical policy gap). We'll inventory technical debt, score business risk, create an upgrade roadmap, and establish architecture standards to prevent it from happening again. Upgrade-proofing is a core competency."
}, {
  q: 'How do you measure application rationalization success?',
  a: 'Three ways: (1) ROI accuracy—do migrated applications deliver predicted savings? (2) Fit accuracy—regret rate on migration decisions. (3) Decommissioning savings—how much did we save from retiring legacy systems vs. migrating them?'
}];
function FAQItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-321">
      <button className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors" onClick={() => setOpen(!open)} data-id="element-322">
        <span className="font-heading font-semibold text-gray-900 text-sm md:text-base pr-4" data-id="element-323">
          {q}
        </span>
        {open ? <ChevronUpIcon className="h-5 w-5 text-nrs-blue flex-shrink-0" data-id="element-324" /> : <ChevronDownIcon className="h-5 w-5 text-nrs-blue flex-shrink-0" data-id="element-325" />}
      </button>
      {open && <div className="px-6 py-4 bg-nrs-light border-t border-gray-100" data-id="element-326">
          <p className="text-gray-700 text-sm leading-relaxed" data-id="element-327">{a}</p>
        </div>}
    </div>;
}
export function HomePage() {
  return <div className="w-full" data-id="element-328">
      {/* HERO */}
      <section className="bg-nrs-navy hero-grid-pattern py-20 px-4" data-id="element-329">
        <div className="max-w-7xl mx-auto" data-id="element-330">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-331">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-332">
              <div className="inline-block px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-4" data-id="element-333">
                ServiceNow Governance & Platform Stewardship
              </div>
              <h1 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-6" data-id="element-334">
                ServiceNow Governance + Expert Development:{' '}
                <span className="text-nrs-orange" data-id="element-335">The Complete Solution</span>
              </h1>
              <p className="text-blue-200 text-lg leading-relaxed mb-8" data-id="element-336">
                ServiceNow Impact drives product vision. NRS provides both
                strategic governance AND expert development. Whether you need
                independent oversight of your SI or a trusted partner to build
                and operate your platform—we deliver both.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-337">
                <Link to="/services" className="px-6 py-3 bg-nrs-orange text-white font-semibold rounded-md hover:bg-amber-500 transition-colors text-center" data-id="element-338">
                  Schedule a Free Platform Assessment
                </Link>
                <Link to="/calculator" className="px-6 py-3 border-2 border-white text-white font-semibold rounded-md hover:bg-white hover:text-nrs-navy transition-colors text-center" data-id="element-339">
                  Calculate Your Technical Debt Risk Score
                </Link>
              </div>
            </motion.div>

            {/* Diagram */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex flex-col items-center gap-2" data-id="element-340">
              {/* ServiceNow Impact */}
              <div className="w-full max-w-sm border-2 border-blue-400 rounded-lg p-4 text-center" data-id="element-341">
                <div className="text-blue-300 font-heading font-bold text-sm" data-id="element-342">
                  ServiceNow Impact
                </div>
                <div className="text-blue-400 text-xs mt-1" data-id="element-343">
                  Product Vision & Adoption Strategy
                </div>
              </div>
              <ArrowDownIcon className="h-6 w-6 text-nrs-orange" data-id="element-344" />
              {/* NRS Box */}
              <div className="w-full max-w-sm bg-nrs-blue rounded-lg p-4" data-id="element-345">
                <div className="text-white font-heading font-bold text-sm text-center mb-3" data-id="element-346">
                  NRS: Governance + Development
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-347">
                  <div className="bg-white/10 rounded p-3" data-id="element-348">
                    <div className="text-nrs-orange text-xs font-bold uppercase mb-2" data-id="element-349">
                      Governance Layer
                    </div>
                    <ul className="text-blue-100 text-xs space-y-1" data-id="element-350">
                      <li data-id="element-351">• Technical Policy</li>
                      <li data-id="element-352">• Data Governance</li>
                      <li data-id="element-353">• Security/ATO</li>
                      <li data-id="element-354">• Strategic Governance</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 rounded p-3" data-id="element-355">
                    <div className="text-nrs-orange text-xs font-bold uppercase mb-2" data-id="element-356">
                      Development Services
                    </div>
                    <ul className="text-blue-100 text-xs space-y-1" data-id="element-357">
                      <li data-id="element-358">• Platform Build</li>
                      <li data-id="element-359">• Module Dev</li>
                      <li data-id="element-360">• Operations</li>
                      <li data-id="element-361">• App Rationalization</li>
                    </ul>
                  </div>
                </div>
              </div>
              <ArrowDownIcon className="h-6 w-6 text-nrs-orange" data-id="element-362" />
              {/* SI Box */}
              <div className="w-full max-w-sm border-2 border-gray-500 rounded-lg p-4 text-center" data-id="element-363">
                <div className="text-gray-300 font-heading font-bold text-sm" data-id="element-364">
                  OR: Your Existing SI
                </div>
                <div className="text-gray-400 text-xs mt-1" data-id="element-365">
                  We govern their work
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="bg-nrs-light py-16 px-4" data-id="element-366">
        <div className="max-w-7xl mx-auto" data-id="element-367">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-368">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-nrs-navy mb-3" data-id="element-369">
              Two Capabilities. One Partner.
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-370">
              Most agencies need both governance and development. NRS delivers
              both under one roof.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-371">
            {/* Governance */}
            <div data-id="element-372">
              <div className="flex items-center gap-3 mb-5" data-id="element-373">
                <div className="h-8 w-1 bg-nrs-orange rounded-full" data-id="element-374" />
                <h3 className="font-heading font-bold text-xl text-nrs-navy" data-id="element-375">
                  Governance Services
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-376">
                {governanceCards.map((card, i) => <motion.div key={card.title} custom={i} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} variants={fadeUp} className="bg-white rounded-lg p-5 shadow-sm border border-gray-100" data-id="element-377">
                    <div className="mb-3" data-id="element-378">{card.icon}</div>
                    <h4 className="font-heading font-semibold text-sm text-gray-900 mb-1" data-id="element-379">
                      {card.title}
                    </h4>
                    <p className="text-gray-500 text-xs leading-relaxed" data-id="element-380">
                      {card.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>

            {/* Development */}
            <div data-id="element-381">
              <div className="flex items-center gap-3 mb-5" data-id="element-382">
                <div className="h-8 w-1 bg-nrs-blue rounded-full" data-id="element-383" />
                <h3 className="font-heading font-bold text-xl text-nrs-navy" data-id="element-384">
                  Development Services
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-385">
                {developmentCards.map((card, i) => <motion.div key={card.title} custom={i} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} variants={fadeUp} className="bg-white rounded-lg p-5 shadow-sm border border-gray-100" data-id="element-386">
                    <div className="mb-3" data-id="element-387">{card.icon}</div>
                    <h4 className="font-heading font-semibold text-sm text-gray-900 mb-1" data-id="element-388">
                      {card.title}
                    </h4>
                    <p className="text-gray-500 text-xs leading-relaxed" data-id="element-389">
                      {card.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="bg-white py-16 px-4" data-id="element-390">
        <div className="max-w-7xl mx-auto" data-id="element-391">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-4" data-id="element-392">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-nrs-navy mb-4" data-id="element-393">
              ServiceNow Impact Has Strategy Covered.
              <br data-id="element-394" />
              <span className="text-nrs-orange" data-id="element-395">
                You Still Need Governance.
              </span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg" data-id="element-396">
              ServiceNow Impact provides excellent product-line vision, adoption
              roadmaps, and strategic planning. But that's not governance. Most
              federal agencies face four critical governance gaps:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10" data-id="element-397">
            {governanceGaps.map((gap, i) => <motion.div key={gap.title} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-red-100 rounded-lg p-6 bg-red-50" data-id="element-398">
                <h3 className="font-heading font-bold text-base text-gray-900 mb-4" data-id="element-399">
                  {gap.title}
                </h3>
                <ul className="space-y-2" data-id="element-400">
                  {gap.items.map(item => <li key={item} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-401">
                      <XCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" data-id="element-402" />
                      {item}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>

          {/* The Result */}
          <div className="mt-12" data-id="element-403">
            <h3 className="font-heading font-bold text-xl text-nrs-navy text-center mb-6" data-id="element-404">
              The Result
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-405">
              {consequences.map((c, i) => <motion.div key={c.label} custom={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200" data-id="element-406">
                  <div className="text-3xl mb-2" data-id="element-407">{c.icon}</div>
                  <div className="font-heading font-semibold text-sm text-gray-900 mb-1" data-id="element-408">
                    {c.label}
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed" data-id="element-409">
                    {c.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="bg-nrs-navy py-16 px-4" data-id="element-410">
        <div className="max-w-7xl mx-auto" data-id="element-411">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-412">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3" data-id="element-413">
              ServiceNow Impact + NRS ={' '}
              <span className="text-nrs-orange" data-id="element-414">Complete Success</span>
            </h2>
            <p className="text-blue-200 max-w-2xl mx-auto" data-id="element-415">
              We provide both strategic governance AND expert ServiceNow
              development. Choose your engagement model:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-416">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl p-6" data-id="element-417">
              <div className="text-nrs-blue font-heading font-bold text-sm uppercase tracking-wider mb-2" data-id="element-418">
                Option 1
              </div>
              <h3 className="font-heading font-bold text-lg text-gray-900 mb-2" data-id="element-419">
                Governance Only
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-420">
                Oversight of your existing SI. We validate, review, and govern
                their work.
              </p>
            </motion.div>
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl p-6" data-id="element-421">
              <div className="text-nrs-blue font-heading font-bold text-sm uppercase tracking-wider mb-2" data-id="element-422">
                Option 2
              </div>
              <h3 className="font-heading font-bold text-lg text-gray-900 mb-2" data-id="element-423">
                Development Only
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-424">
                We build, you govern. For organizations with strong internal
                governance capability.
              </p>
            </motion.div>
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-nrs-orange rounded-xl p-6 relative" data-id="element-425">
              <div className="absolute top-3 right-3 bg-white text-nrs-orange text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-426">
                Most Popular
              </div>
              <div className="text-white font-heading font-bold text-sm uppercase tracking-wider mb-2" data-id="element-427">
                Option 3
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-2" data-id="element-428">
                Governance + Development
              </h3>
              <p className="text-orange-100 text-sm" data-id="element-429">
                Complete solution. Self-governing development with built-in
                architecture standards.
              </p>
            </motion.div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white/5 rounded-xl overflow-hidden border border-white/10" data-id="element-430">
            <div className="grid grid-cols-2 bg-white/10" data-id="element-431">
              <div className="px-6 py-3 font-heading font-bold text-sm text-red-300" data-id="element-432">
                ❌ Traditional Approach
              </div>
              <div className="px-6 py-3 font-heading font-bold text-sm text-green-300" data-id="element-433">
                ✅ NRS Model
              </div>
            </div>
            {comparisonRows.map((row, i) => <div key={i} className={`grid grid-cols-2 ${i % 2 === 0 ? 'bg-white/5' : ''}`} data-id="element-434">
                <div className="px-6 py-4 text-sm text-red-200 border-r border-white/10" data-id="element-435">
                  {row.traditional}
                </div>
                <div className="px-6 py-4 text-sm text-green-200" data-id="element-436">
                  {row.nrs}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="bg-white py-16 px-4" data-id="element-437">
        <div className="max-w-7xl mx-auto" data-id="element-438">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-439">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-nrs-navy mb-3" data-id="element-440">
              Three Engagement Models
            </h2>
            <p className="text-gray-600" data-id="element-441">
              Choose the model that fits your organization's needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-442">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6" data-id="element-443">
              <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-444">
                Model 1
              </div>
              <h3 className="font-heading font-bold text-lg text-nrs-navy mb-4" data-id="element-445">
                Governance Oversight
              </h3>
              <div className="space-y-3 mb-5" data-id="element-446">
                <div className="flex gap-2 text-sm" data-id="element-447">
                  <span className="text-blue-500 font-semibold w-24 flex-shrink-0" data-id="element-448">
                    Impact:
                  </span>
                  <span className="text-gray-600" data-id="element-449">Product Vision</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-450">
                  <span className="text-gray-500 font-semibold w-24 flex-shrink-0" data-id="element-451">
                    Your SI:
                  </span>
                  <span className="text-gray-600" data-id="element-452">Implementation</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-453">
                  <span className="text-nrs-orange font-semibold w-24 flex-shrink-0" data-id="element-454">
                    NRS:
                  </span>
                  <span className="text-gray-600" data-id="element-455">
                    Governance (technical policy, data, security/ATO, strategic)
                  </span>
                </div>
              </div>
              <div className="bg-nrs-light rounded-lg p-3" data-id="element-456">
                <div className="text-xs font-semibold text-nrs-blue mb-1" data-id="element-457">
                  Best For:
                </div>
                <p className="text-xs text-gray-600" data-id="element-458">
                  Organizations with existing SI relationships who need
                  independent validation
                </p>
              </div>
            </motion.div>

            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl p-6" data-id="element-459">
              <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-460">
                Model 2
              </div>
              <h3 className="font-heading font-bold text-lg text-nrs-navy mb-4" data-id="element-461">
                Development Services
              </h3>
              <div className="space-y-3 mb-5" data-id="element-462">
                <div className="flex gap-2 text-sm" data-id="element-463">
                  <span className="text-blue-500 font-semibold w-24 flex-shrink-0" data-id="element-464">
                    Impact:
                  </span>
                  <span className="text-gray-600" data-id="element-465">Product Vision</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-466">
                  <span className="text-nrs-orange font-semibold w-24 flex-shrink-0" data-id="element-467">
                    NRS:
                  </span>
                  <span className="text-gray-600" data-id="element-468">
                    Implementation (platform dev, integrations, operations)
                  </span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-469">
                  <span className="text-gray-500 font-semibold w-24 flex-shrink-0" data-id="element-470">
                    Your Team:
                  </span>
                  <span className="text-gray-600" data-id="element-471">Governance</span>
                </div>
              </div>
              <div className="bg-nrs-light rounded-lg p-3" data-id="element-472">
                <div className="text-xs font-semibold text-nrs-blue mb-1" data-id="element-473">
                  Best For:
                </div>
                <p className="text-xs text-gray-600" data-id="element-474">
                  Organizations with strong internal governance capability
                  needing development capacity
                </p>
              </div>
            </motion.div>

            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border-2 border-nrs-orange rounded-xl p-6 relative" data-id="element-475">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-nrs-orange text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-476">
                ⭐ Most Popular
              </div>
              <div className="text-nrs-orange font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-477">
                Model 3
              </div>
              <h3 className="font-heading font-bold text-lg text-nrs-navy mb-4" data-id="element-478">
                Governance + Development
              </h3>
              <div className="space-y-3 mb-5" data-id="element-479">
                <div className="flex gap-2 text-sm" data-id="element-480">
                  <span className="text-blue-500 font-semibold w-24 flex-shrink-0" data-id="element-481">
                    Impact:
                  </span>
                  <span className="text-gray-600" data-id="element-482">Product Vision</span>
                </div>
                <div className="flex gap-2 text-sm" data-id="element-483">
                  <span className="text-nrs-orange font-semibold w-24 flex-shrink-0" data-id="element-484">
                    NRS:
                  </span>
                  <span className="text-gray-600" data-id="element-485">
                    BOTH Governance AND Implementation
                  </span>
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 mb-4" data-id="element-486">
                <div className="text-xs font-semibold text-nrs-orange mb-1" data-id="element-487">
                  Best For:
                </div>
                <p className="text-xs text-gray-600" data-id="element-488">
                  Small-to-medium agencies needing complete platform stewardship
                </p>
              </div>
              <p className="text-xs text-gray-500 italic" data-id="element-489">
                Self-governing development: every project follows our own
                architecture standards, data governance, and ATO requirements
              </p>
            </motion.div>
          </div>

          {/* App Rationalization Callout */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-10 border-l-4 border-nrs-orange bg-orange-50 rounded-r-xl p-6" data-id="element-490">
            <h3 className="font-heading font-bold text-lg text-nrs-navy mb-3" data-id="element-491">
              The Application Rationalization Differentiator
            </h3>
            <p className="text-gray-700 text-sm mb-4" data-id="element-492">
              Unlike SIs who want to migrate everything to ServiceNow, we
              provide independent assessment of your application portfolio:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-493">
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-494">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-495" />
                Which applications are a strong ServiceNow fit (we'll build
                these)
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-496">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-497" />
                Which should remain standalone (don't force it)
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-498">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-499" />
                Which can be decommissioned entirely (cost savings)
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700" data-id="element-500">
                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-501" />
                What the ROI and implementation sequence should be
              </div>
            </div>
            <p className="text-sm text-nrs-orange font-semibold mt-4" data-id="element-502">
              Outcome: You invest in ServiceNow migrations that deliver 3:1 ROI,
              not projects that create technical debt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROOF POINTS */}
      <section className="bg-nrs-light py-16 px-4" data-id="element-503">
        <div className="max-w-7xl mx-auto" data-id="element-504">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-505">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-nrs-navy mb-3" data-id="element-506">
              What Good Governance Delivers
            </h2>
            <p className="text-gray-600" data-id="element-507">
              Client outcomes from recent engagements
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-508">
            {proofPoints.map((p, i) => <motion.div key={p.metric} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-509">
                <div className="text-nrs-orange font-heading font-bold text-lg mb-1" data-id="element-510">
                  {p.result}
                </div>
                <div className="text-gray-600 text-xs" data-id="element-511">{p.metric}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-white py-16 px-4" data-id="element-512">
        <div className="max-w-7xl mx-auto" data-id="element-513">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-514">
            <div data-id="element-515">
              <h2 className="font-heading font-bold text-3xl text-nrs-navy mb-6" data-id="element-516">
                Who We Serve
              </h2>
              <div className="space-y-3" data-id="element-517">
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-518">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-519" />
                  Federal agencies (DoD, IC, civilian) with 500-5,000 ServiceNow
                  users
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-520">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-521" />
                  State/local governments and higher education institutions
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-522">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-523" />
                  Organizations expanding beyond ITSM into HRSD, Security
                  Operations, or Strategic Portfolio Management
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-524">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-525" />
                  Platforms stuck on old versions due to heavy customization
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-700" data-id="element-526">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-527" />
                  Agencies under budget scrutiny needing to demonstrate
                  ServiceNow ROI
                </div>
              </div>
            </div>
            <div data-id="element-528">
              <h3 className="font-heading font-bold text-xl text-nrs-navy mb-5" data-id="element-529">
                Recent Engagements
              </h3>
              <div className="space-y-4" data-id="element-530">
                <div className="border border-nrs-blue/20 rounded-xl p-5 bg-nrs-light" data-id="element-531">
                  <div className="font-heading font-bold text-nrs-blue text-sm mb-1" data-id="element-532">
                    Defense Intelligence Agency (DIA)
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-533">
                    NIPR & JWICS platform governance — cross-domain data
                    synchronization and ATO compliance
                  </p>
                </div>
                <div className="border border-nrs-blue/20 rounded-xl p-5 bg-nrs-light" data-id="element-534">
                  <div className="font-heading font-bold text-nrs-blue text-sm mb-1" data-id="element-535">
                    Army War College
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-536">
                    Academic institution platform stewardship — governance
                    framework and application rationalization
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 py-16 px-4" data-id="element-537">
        <div className="max-w-4xl mx-auto" data-id="element-538">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-539">
            <h2 className="font-heading font-bold text-3xl text-nrs-navy mb-3" data-id="element-540">
              Frequently Asked Questions
            </h2>
          </motion.div>
          <div className="space-y-3" data-id="element-541">
            {faqs.map(faq => <FAQItem key={faq.q} q={faq.q} a={faq.a} data-id="element-542" />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-nrs-navy py-16 px-4" data-id="element-543">
        <div className="max-w-4xl mx-auto text-center" data-id="element-544">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-545">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4" data-id="element-546">
              Ready to Close the Governance Gap?
            </h2>
            <p className="text-blue-200 text-lg mb-8" data-id="element-547">
              Let's talk about your platform. Free 30-minute assessment, no
              commitment required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-548">
              <Link to="/services" className="px-8 py-3 bg-nrs-orange text-white font-semibold rounded-md hover:bg-amber-500 transition-colors" data-id="element-549">
                Schedule a Free Assessment
              </Link>
              <a href="#" className="px-8 py-3 border-2 border-white text-white font-semibold rounded-md hover:bg-white hover:text-nrs-navy transition-colors" data-id="element-550">
                Download White Paper
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}